import { ref } from 'vue';
import { supabase, supabaseAdmin } from '@/services/supabase';
import { logger } from '@/services/logger';

/**
 * Sanitize a filename to ensure it's compatible with Supabase Storage
 * Uses URL encoding to handle special characters and non-ASCII characters
 */
function sanitizeFileName(fileName: string): string {
  // Split the filename into name and extension
  const lastDotIndex = fileName.lastIndexOf('.');
  let name = fileName;
  let extension = '';
  
  if (lastDotIndex !== -1) {
    name = fileName.substring(0, lastDotIndex);
    extension = fileName.substring(lastDotIndex);
  }
  
  // URL encode the name part (but keep the extension as is)
  let encodedName = encodeURIComponent(name);
  
  // Replace certain characters that might still cause issues
  encodedName = encodedName.replace(/%/g, '_');
  
  // Combine encoded name with the original extension
  let sanitized = encodedName + extension;
  
  // Ensure the filename is not too long (max 255 characters)
  if (sanitized.length > 255) {
    // If too long, truncate the name part, not the extension
    const maxNameLength = 255 - extension.length;
    sanitized = encodedName.substring(0, maxNameLength) + extension;
  }
  
  return sanitized;
}

export function useStorage() {
  const loading = ref(false);
  const error = ref<Error | null>(null);

  /**
   * Upload a file to Supabase Storage
   */
  async function uploadFile(bucket: string, path: string, file: File) {
    loading.value = true;
    
    try {
      // Sanitize the file path to prevent invalid character errors
      const sanitizedPath = sanitizeFileName(path);
      
      if (sanitizedPath !== path) {
        logger.info('File path sanitized', { original: path, sanitized: sanitizedPath });
      }
      
      // Try to use the admin client first to bypass RLS policies
      if (supabaseAdmin) {
        try {
          logger.info('Using admin client for file upload to bypass RLS');
          
          const { data, error: adminErr } = await supabaseAdmin.storage
            .from(bucket)
            .upload(sanitizedPath, file);
          
          if (!adminErr) {
            return {
              ...data,
              path: sanitizedPath // Return the sanitized path for reference
            };
          }
          
          // If admin client fails, log the error but continue with regular client
          logger.warn('Admin client upload failed, falling back to regular client', { error: adminErr });
        } catch (adminErr) {
          logger.warn('Error using admin client for upload', { error: adminErr });
          // Continue with regular client
        }
      }
      
      // Use regular client as fallback
      const { data, error: err } = await supabase.storage
        .from(bucket)
        .upload(sanitizedPath, file);
      
      if (err) {
        // Handle specific error types
        const errorObj = err as any; // Type assertion to access any properties
        if (errorObj.statusCode === '404' && errorObj.message === 'Bucket not found') {
          const errorMsg = `Bucket "${bucket}" not found. Please create it in the Supabase dashboard.`;
          logger.error(errorMsg, { bucket });
          throw new Error(errorMsg);
        } else if (errorObj.statusCode === '403' && errorObj.message.includes('row-level security policy')) {
          const errorMsg = `Permission denied due to RLS policy. Make sure you have the correct permissions or try using the admin client.`;
          logger.error(errorMsg, { bucket });
          throw new Error(errorMsg);
        }
        throw err;
      }
      
      return {
        ...data,
        path: sanitizedPath // Return the sanitized path for reference
      };
    } catch (err) {
      logger.error('File upload error:', { error: err });
      
      // Create a more user-friendly error message
      let errorMessage = 'File upload failed';
      
      if (typeof err === 'object' && err !== null) {
        const errorObj = err as any;
        if (errorObj.statusCode === '404' && errorObj.message === 'Bucket not found') {
          errorMessage = `Bucket "${bucket}" not found. Please create it in the Supabase dashboard.`;
        } else if (errorObj.message) {
          errorMessage = errorObj.message;
        }
      }
      
      error.value = new Error(errorMessage);
      throw error.value;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Download a file from Supabase Storage
   */
  async function downloadFile(bucket: string, path: string) {
    loading.value = true;
    
    try {
      // Try to use the admin client first to bypass RLS policies
      if (supabaseAdmin) {
        try {
          logger.info('Using admin client for file download to bypass RLS');
          
          const { data, error: adminErr } = await supabaseAdmin.storage
            .from(bucket)
            .download(path);
          
          if (!adminErr) {
            return data;
          }
          
          // If admin client fails, log the error but continue with regular client
          logger.warn('Admin client download failed, falling back to regular client', { error: adminErr });
        } catch (adminErr) {
          logger.warn('Error using admin client for download', { error: adminErr });
          // Continue with regular client
        }
      }
      
      // Use regular client as fallback
      const { data, error: err } = await supabase.storage
        .from(bucket)
        .download(path);
      
      if (err) throw err;
      
      return data;
    } catch (err) {
      logger.error('File download error', { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Get a public URL for a file
   */
  function getPublicUrl(bucket: string, path: string) {
    // Try to use the admin client first to bypass RLS policies
    if (supabaseAdmin) {
      try {
        const { data } = supabaseAdmin.storage
          .from(bucket)
          .getPublicUrl(path);
        
        return data.publicUrl;
      } catch (err) {
        logger.warn('Error using admin client for getPublicUrl, falling back to regular client', { error: err });
        // Continue with regular client
      }
    }
    
    // Use regular client as fallback
    const { data } = supabase.storage
      .from(bucket)
      .getPublicUrl(path);
    
    return data.publicUrl;
  }

  /**
   * List all files in a bucket/folder
   */
  async function listFiles(bucket: string, path?: string) {
    loading.value = true;
    
    try {
      // Try to use the admin client first to bypass RLS policies
      if (supabaseAdmin) {
        try {
          logger.info('Using admin client for file listing to bypass RLS');
          
          const { data, error: adminErr } = await supabaseAdmin.storage
            .from(bucket)
            .list(path);
          
          if (!adminErr) {
            return data;
          }
          
          // If admin client fails, log the error but continue with regular client
          logger.warn('Admin client listing failed, falling back to regular client', { error: adminErr });
        } catch (adminErr) {
          logger.warn('Error using admin client for listing', { error: adminErr });
          // Continue with regular client
        }
      }
      
      // Use regular client as fallback
      const { data, error: err } = await supabase.storage
        .from(bucket)
        .list(path);
      
      if (err) throw err;
      
      return data;
    } catch (err) {
      logger.error('File listing error', { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Delete a file from storage
   */
  async function deleteFile(bucket: string, path: string) {
    loading.value = true;
    
    try {
      // Try to use the admin client first to bypass RLS policies
      if (supabaseAdmin) {
        try {
          logger.info('Using admin client for file deletion to bypass RLS');
          
          const { error: adminErr } = await supabaseAdmin.storage
            .from(bucket)
            .remove([path]);
          
          if (!adminErr) {
            return true;
          }
          
          // If admin client fails, log the error but continue with regular client
          logger.warn('Admin client deletion failed, falling back to regular client', { error: adminErr });
        } catch (adminErr) {
          logger.warn('Error using admin client for deletion', { error: adminErr });
          // Continue with regular client
        }
      }
      
      // Use regular client as fallback
      const { error: err } = await supabase.storage
        .from(bucket)
        .remove([path]);
      
      if (err) throw err;
      
      return true;
    } catch (err) {
      logger.error('File deletion error', { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }

  return {
    loading,
    error,
    uploadFile,
    downloadFile,
    getPublicUrl,
    listFiles,
    deleteFile
  };
}